import React from "react";
import { Box, Flex, Text, Badge, Avatar } from "@radix-ui/themes";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { Minus } from "@geist-ui/icons";
import { format } from "date-fns";
import styles from "./styles.module.scss";
import { GeistDrawer } from "@/components/geistDrawer";

interface ConversationHistoryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversation: any;
}

export function ConversationHistory({
  open,
  onOpenChange,
  conversation,
}: ConversationHistoryProps) {
  if (!conversation) return null;

  const handleClose = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const filteredComments = conversation.releaseComments?.filter(
    (comment: any) =>
      !comment.comment.toLowerCase().includes("reset") &&
      !comment.comment.includes("--")
  );

  const formatMessage = (message: string) => {
    if (message === "talk_to_an_agent") {
      return "Quack will escalate it to an agent";
    }
    return message;
  };

  return (
    <GeistDrawer
      openDrawer={open}
      onDrawerClosed={handleClose}
      disableBackdropClick={false}
      headline={
        <Text size="5" weight="bold">
          Session History
        </Text>
      }
    >
      <Box className={styles.drawerContainer}>
        <Box className={styles.messagesContainer}>
          {filteredComments?.map((comment: any, index: number) => (
            <Flex
              key={index}
              direction="column"
              className={`${styles.messageWrapper} ${
                comment.isAgent ? styles.agent : styles.user
              }`}
            >
              <Flex
                align="center"
                gap="2"
                className={
                  comment.isAgent ? styles.agentLabel : styles.userLabel
                }
              >
                <Box className={styles.avatarWrapper}>
                  <Avatar
                    size="2"
                    fallback={comment.isAgent ? "Q" : "U"}
                    className={
                      comment.isAgent ? styles.agentAvatar : styles.userAvatar
                    }
                    radius="full"
                  />
                </Box>
                <Text size="2" weight="medium">
                  {comment.isAgent ? "Quack" : "User"}
                </Text>
              </Flex>
              <Box
                className={
                  comment.isAgent ? styles.agentMessage : styles.userMessage
                }
              >
                <Text
                  size="2"
                  align="left"
                  className={
                    comment.isAgent ? styles.agentText : styles.userText
                  }
                >
                  {formatMessage(comment.comment)}
                </Text>
              </Box>
            </Flex>
          ))}

          <div className={styles.activityLog}>
            <div className={styles.activityItem}>
              <Badge
                size="1"
                color={
                  conversation.feedback === "POSITIVE"
                    ? "green"
                    : conversation.feedback === "NEGATIVE"
                    ? "red"
                    : "gray"
                }
                className={styles.feedbackBadge}
              >
                {conversation.feedback === "POSITIVE" ? (
                  <ThumbsUp size={12} />
                ) : conversation.feedback === "NEGATIVE" ? (
                  <ThumbsDown size={12} />
                ) : (
                  <Minus size={12} />
                )}
              </Badge>
              <Text size="2" color="gray">
                {conversation.feedback === "POSITIVE"
                  ? "Good"
                  : conversation.feedback === "NEGATIVE"
                  ? "Bad"
                  : "No feedback"}{" "}
                • {format(new Date(conversation.createdAt), "MMM d, h:mm a")}
              </Text>
            </div>
          </div>
        </Box>
      </Box>
    </GeistDrawer>
  );
}
